import React from 'react';
import RequiredApiNotice from './RequiredApiNotice';

interface SizeDashboardProps {
  data: any;
  address: any;
}

const SizeDashboard: React.FC<SizeDashboardProps> = ({ data, address }) => {
  if (!data || data.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['US Census ACS 5-Year API', 'US Census Decennial API']} />
      </div>
    );
  }

  // Map backend fields (see sample payload) into the table shape used in PDFs:
  // Neighborhood → tract, City → city, County → county, Metro → metroArea, State → state.
  const neighborhoodPopulation = data.tract?.population ?? null;
  const cityPopulation = data.city?.population ?? null;
  const countyPopulation = data.county?.population ?? null;
  const metroPopulation = data.metroArea?.population ?? null;
  const statePopulation = data.state?.population ?? null;

  const neighborhoodHouseholds = data.tract?.households ?? null;
  const cityHouseholds = data.city?.households ?? null;
  const countyHouseholds = data.county?.households ?? null;
  const metroHouseholds = data.metroArea?.households ?? null;
  const stateHouseholds = data.state?.households ?? null;

  const currentPopulation = neighborhoodPopulation ?? countyPopulation ?? null;
  const householdCount = neighborhoodHouseholds ?? countyHouseholds ?? null;
  const missingFields = [];
  if (!currentPopulation) missingFields.push('Current Population');
  if (!householdCount) missingFields.push('Household Count');
  if (!data.marketSizeScore) missingFields.push('Market Size Score');

  const marketTypeRows: Array<{
    type: string;
    characteristics: string;
    populationRange: string;
  }> =
    data.metroAreaMarketTypes ||
    data.metroArea?.marketTypes || [
      {
        type: 'Primary',
        characteristics:
          'Primary markets are the largest housing markets in the country, also known as gateway markets. Popular examples: New York City, Boston, Los Angeles, Atlanta, Chicago, Miami, Seattle.',
        populationRange: '4M+',
      },
      {
        type: 'Secondary',
        characteristics:
          'Slightly smaller than primary markets with less economic activity, but typically growing in terms of local commerce, industry and population. More affordable and less densely populated.',
        populationRange: '2M – 4M',
      },
      {
        type: 'Tertiary',
        characteristics:
          'Even smaller than secondary markets, with a more spread out population. They may have fewer amenities but attract renters and homeowners because of affordability and quality of life.',
        populationRange: '1M – 2M',
      },
      {
        type: 'Quaternary',
        characteristics:
          'Mid-sized markets with regional economic significance.',
        populationRange: '200K – 1M',
      },
      {
        type: 'Quinary',
        characteristics: 'Small markets, often rural or suburban in nature.',
        populationRange: '< 200K',
      },
    ];

  const resolveMarketTypeFromPopulation = (population: number | null | undefined): string | null => {
    if (population == null) return null;
    const p = typeof population === 'number' ? population : parseFloat(String(population));
    if (Number.isNaN(p)) return null;
    if (p >= 4_000_000) return 'Primary';
    if (p >= 2_000_000) return 'Secondary';
    if (p >= 1_000_000) return 'Tertiary';
    if (p >= 200_000) return 'Quaternary';
    return 'Quinary';
  };

  const activeMarketType = resolveMarketTypeFromPopulation(metroPopulation);

  const formatNumber = (value: any) => {
    if (value === null || value === undefined) return 'N/A';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(num)) return 'N/A';
    return num.toLocaleString();
  };

  const labels = data.locationLabels || {};
  const isCensusTractPlaceLabel = (label?: string | null) => {
    const s = String(label || '').trim();
    if (!s) return false;
    return /^census\s+tract\b/i.test(s) || /^tract\s+[\d.]+$/i.test(s);
  };
  const neighborhoodSubtitle = [address?.neighborhood, labels.neighborhood].find(
    (v) => v && !isCensusTractPlaceLabel(v),
  );
  const columnLabel = (title: string, subtitle?: string | null) =>
    subtitle ? (
      <>
        <span>{title}</span>
        <span style={{ display: 'block', fontSize: 10, fontWeight: 500, color: '#6b7280' }}>
          {subtitle}
        </span>
      </>
    ) : (
      title
    );

  return (
    <div className="evaluation-section">
      <header className="evaluation-section-header">
        <div>
          <p className="dashboard-eyebrow">S • Size</p>
          <h2>S - SIZE (Market Size & Scale)</h2>
          <p className="evaluation-section-subtitle">
            Is this market big enough to support long-term investing?
          </p>
          {data.metroAreaName && (
            <p className="evaluation-section-subtitle">
              Metro area — {data.metroAreaName}
            </p>
          )}
        </div>
      </header>

      <div className="wti-metric-table">
        <div className="wti-metric-header-row" style={{ gridTemplateColumns: '170px repeat(5, minmax(0, 1fr))' }}>
          <div className="wti-metric-label-cell" />
          <div className="wti-metric-header-cell">
            <span style={{ display: 'block', width: '100%', textAlign: 'left' }}>
              {columnLabel('Neighborhood', neighborhoodSubtitle)}
            </span>
          </div>
          <div className="wti-metric-header-cell">
            <span style={{ display: 'block', width: '100%', textAlign: 'left' }}>
              {columnLabel('City', labels.city || address?.city || data.city?.name)}
            </span>
          </div>
          <div className="wti-metric-header-cell">
            <span style={{ display: 'block', width: '100%', textAlign: 'left' }}>
              {columnLabel('County', labels.county || data.county?.name || address?.county)}
            </span>
          </div>
          <div className="wti-metric-header-cell">
            <span style={{ display: 'block', width: '100%', textAlign: 'left' }}>
              {columnLabel('Metro area', labels.metroArea || data.metroAreaName)}
            </span>
          </div>
          <div className="wti-metric-header-cell">
            <span style={{ display: 'block', width: '100%', textAlign: 'left' }}>
              {columnLabel('State', labels.state || address?.state)}
            </span>
          </div>
        </div>

        <div className="wti-metric-row" style={{ gridTemplateColumns: '170px repeat(5, minmax(0, 1fr))' }}>
          <div className="wti-metric-label-cell">Population</div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(neighborhoodPopulation)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(cityPopulation)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(countyPopulation)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(metroPopulation)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(statePopulation)}</span></div>
        </div>

        <div className="wti-metric-row" style={{ gridTemplateColumns: '170px repeat(5, minmax(0, 1fr))' }}>
          <div className="wti-metric-label-cell">Households</div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(neighborhoodHouseholds)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(cityHouseholds)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(countyHouseholds)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(metroHouseholds)}</span></div>
          <div className="wti-metric-value-cell"><span style={{ display: 'block', width: '100%', textAlign: 'left' }}>{formatNumber(stateHouseholds)}</span></div>
        </div>
      </div>

      <div className="wti-market-section card">
        <div className="card-header">
          <h3>Metro area market type</h3>
        </div>
        <div className="wti-market-table">
          <div className="wti-market-header-row">
            <div className="wti-market-col-type">Market type</div>
            <div className="wti-market-col-characteristics">Market characteristics</div>
            <div className="wti-market-col-range">Population range</div>
          </div>
          {marketTypeRows.length === 0 ? (
            <div className="wti-market-row">
              <div className="wti-market-col-type">N/A</div>
              <div className="wti-market-col-characteristics">
                Metro area market type data not available.
              </div>
              <div className="wti-market-col-range">N/A</div>
            </div>
          ) : (
            marketTypeRows.map((row, index) => (
              <div
                key={`${row.type}-${index}`}
                className={`wti-market-row${
                  activeMarketType &&
                  String(row.type).toLowerCase() === activeMarketType.toLowerCase()
                    ? ' wti-market-row--highlight'
                    : ''
                }`}
              >
                <div className="wti-market-col-type">{row.type}</div>
                <div className="wti-market-col-characteristics">
                  {row.characteristics}
                </div>
                <div className="wti-market-col-range">
                  {row.populationRange || '—'}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {missingFields.length > 0 && (
        <RequiredApiNotice apis={['US Census ACS 5-Year API', 'US Census Decennial API']} missingFields={missingFields} />
      )}
    </div>
  );
};

export default SizeDashboard;
